import { SkywardException, reportException } from "#skyward_error";
import { CallbackStore, FacadeFor, FacadesForCollection } from "./types";

/**
 * @description Get a CallbackStore object that can be used to manage and dispatch callback functions.
 * @returns {CallbackStore} An object allowing management and dispatching of Callback functions.
 */
export const getCallbackStore = function (): CallbackStore<(...args: any[]) => void | any>
{
    const callbacks: Array<{ callback: (...args: any[]) => void | any, priority: number }> = [];

    const callbackStore =
    {
        registerCallback: function (callback: (...args: any[]) => void | any, priority?: number)
        {
            const p = priority ?? Number.MAX_SAFE_INTEGER;
            let insertIndex = callbacks.length;
            for (let i = 0; i < callbacks.length; i++)
            {
                if (callbacks[i].priority > p)
                {
                    insertIndex = i;
                    break;
                }
            }
            callbacks.splice(insertIndex, 0, { callback, priority: p });
        },
        deregisterCallback: function (callback: (...args: any[]) => void | any)
        {
            for (let i = callbacks.length - 1; i >= 0; i--)
            {
                if (callbacks[i].callback === callback)
                {
                    callbacks.splice(i, 1);
                }
            }
        },
        getCallbacks: function ()
        {
            return callbacks.map(entry => entry.callback);
        },
        /**
        * @description Call all of the callbacks in a given CallbackStore.
        */
        dispatchCallbacks: function (...args: any[])
        {
            for (let i = 0; i < callbacks.length; i++)
            {
                try
                {
                    callbacks[i].callback.call(this, ...args);
                }
                catch (ex)
                {
                    if (ex instanceof Error)
                    {
                        reportException(ex);
                    }
                    else
                    {
                        reportException(new SkywardException(ex.toString()));
                    }
                }
            }
        }
    };

    return callbackStore;
};

export let getCallbackStoreFacade = function <T extends CallbackStore<any>>(callbackStore: T): FacadeFor<T>
{
    return {
        registerCallback: callbackStore.registerCallback,
        deregisterCallback: callbackStore.deregisterCallback,
    } as FacadeFor<T>;
}

/**
 * @description Convert an internal/private CallbackStoreCollection into an external/public CallbackStoreFacadeCollection
 * @returns {CallbackStoreFacadeCollection} A collection of CallbackStores with limited public properties.
 */
export let getCallbackStoreFacadeCollection = function <T extends { [key: string]: CallbackStore<any>; }>(callbackStoreCollection: T): FacadesForCollection<T>
{
    const callbackStoreFacadeCollection: Partial<{ [key: string]: FacadeFor<any>; }> = {};

    for (let i in callbackStoreCollection)
    {
        callbackStoreFacadeCollection[i] = getCallbackStoreFacade(callbackStoreCollection[i]);
    }

    return Object.freeze(callbackStoreFacadeCollection) as FacadesForCollection<T>;
};